import type { ResidenceManagerPerson } from '@repo/sdk/gen'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { Controller, useFormContext } from 'react-hook-form'
import { type TKey, useTranslation } from '@/lib/i18n'

type ManagerForm = Pick<
	ResidenceManagerPerson,
	'firstName' | 'lastName' | 'email'
>

/**
 * The person a manager account belongs to: first name, last name and personal
 * address, in the form of the page that invites them.
 */
function ManagerFields() {
	const { t } = useTranslation()
	return (
		<FieldGroup>
			<div className='grid gap-4 sm:grid-cols-2'>
				<TextField
					autoComplete='given-name'
					label={t('admin-residences:managers.first_name')}
					name='firstName'
				/>
				<TextField
					autoComplete='family-name'
					label={t('admin-residences:managers.last_name')}
					name='lastName'
				/>
			</div>
			<TextField
				autoComplete='email'
				label={t('admin-residences:managers.email')}
				name='email'
				type='email'
			/>
		</FieldGroup>
	)
}

function TextField({
	name,
	label,
	type = 'text',
	autoComplete
}: {
	name: keyof ManagerForm
	label: string
	type?: 'text' | 'email'
	autoComplete: string
}) {
	const { control } = useFormContext<ManagerForm>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<Field data-invalid={fieldState.invalid}>
					<FieldLabel htmlFor={`manager-${name}`}>{label}</FieldLabel>
					<Input
						{...field}
						aria-invalid={fieldState.invalid}
						autoComplete={autoComplete}
						id={`manager-${name}`}
						type={type}
					/>
					{fieldState.invalid && (
						<FieldError errors={[fieldState.error]} />
					)}
				</Field>
			)}
		/>
	)
}

/** The message of a refusal that concerns the address, shown on the email field. */
function managerEmailError(error: unknown) {
	return typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		typeof error.code === 'string'
		? EMAIL_ERROR[error.code]
		: undefined
}

const EMAIL_ERROR: Partial<Record<string, TKey>> = {
	email_in_use: 'admin-residences:managers.email_in_use',
	manager_already_assigned: 'admin-residences:managers.already_assigned',
	manager_disabled: 'admin-residences:managers.manager_disabled'
}

export { ManagerFields, managerEmailError }
